<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { PageHeader, Button, Badge, Modal, Drawer } from '@drama/ui'
import { ArrowLeft, RefreshCw, Clock, AlertTriangle, Film } from '@lucide/vue'

import StoryFilmListView from '../components/story-film/StoryFilmListView.vue'
import StoryFilmDetailStage from '../components/story-film/StoryFilmDetailStage.vue'
import StoryFilmShotInspector from '../components/story-film/StoryFilmShotInspector.vue'
import PreparationOverview from '../components/story-film/PreparationOverview.vue'
import IndependentProductionView from '../components/story-film/IndependentProductionView.vue'
import PromptPackHistory from '../components/story-film/PromptPackHistory.vue'

import { useStoryFilmList } from '../composables/story-film/useStoryFilmList.js'
import { useStoryFilmDetail } from '../composables/story-film/useStoryFilmDetail.js'
import { useStoryFilmPlayer } from '../composables/story-film/useStoryFilmPlayer.js'
import {
  getStoryFilmExecution,
  getStoryFilmExecutionMedia,
  getStoryFilmAgentPreparation,
  getStoryFilmPreparationReference,
  getStoryFilmPreparationSample,
  listStoryFilmAgentRevisions,
  getStoryFilmAgentRevision,
  getStoryFilmAgentRevisionFilm,
  getStoryFilmAgentRevisionMedia,
  getStoryFilmAgentRevisionReference,
} from '../api/story-film-prompt-packs.js'

const { t } = useI18n()
const route = useRoute()

// 1. 列表状态 composable
const {
  workspaceMode,
  executionItems,
  executionListLoading,
  executionListError,
  preparations,
  preparationLoading,
  preparationError,
  searchKeyword,
  selectedFilm,
  selectedStatusFilter,
  directInputId,
  filmOptions,
  isFilterActive,
  filteredExecutions,
  groupedExecutions,
  filteredPreparations,
  viewMode,
  loadExecutions,
  loadPreparations,
  resetFilters,
  switchWorkspace,
} = useStoryFilmList()

// 2. 详情状态 composable
const {
  selectedExecutionId,
  execution,
  executionLoading,
  executionError,
  executionSnapshot,
  predictedCurrency,
  selectedShotIndex,
  selectedShot,
  selectedScene,
  evidenceGroups,
  frozenPassages,
  castRoleList,
  sourceStoryHref,
  promptComparison,
  referenceUrls,
  referenceStates,
  referenceErrors,
  selectedPreparation,
  selectedPreparationDetail,
  independentRecord,
  independentLoading,
  independentError,
  availableRevisions,
  revisionName,
  revisionLoading,
  revisionError,
  reconcileReferences,
  loadSelectedReferences,
  loadExecutionCurrency,
  selectShot,
  clearReferenceUrls,
  stopExecutionPolling,
  disposeDetail,
  recordErrorText,
} = useStoryFilmDetail()

// 3. 视频播放器 composable
const {
  executionVideo,
  executionVideoUrl,
  currentVideoTime,
  mediaState,
  mediaError,
  totalShotsDurationMs,
  playheadPercent,
  onTimeUpdate,
  onVideoMetadata,
  onVideoReady,
  onVideoError,
  togglePlay,
  seekPlannedShot,
  clearVideo,
} = useStoryFilmPlayer(execution, selectedShot)

const showHistoryDrawer = ref(false)
const previewImageModal = ref(null)
const selectedDetailTab = ref('prompt')
const sampleVideoUrl = ref('')
const sampleVideo = ref(null)
const sampleLoading = ref(false)
const sampleError = ref(false)
const samplePlaying = ref(false)
let sampleRequest = 0

function clearSample() {
  sampleRequest++
  if (sampleVideoUrl.value) URL.revokeObjectURL(sampleVideoUrl.value)
  sampleVideoUrl.value = ''
  sampleLoading.value = false
  sampleError.value = false
  samplePlaying.value = false
}

async function playSample() {
  try {
    await sampleVideo.value?.play()
  } catch {
    sampleError.value = true
  }
}

async function loadSample() {
  const request = ++sampleRequest
  sampleLoading.value = true
  sampleError.value = false
  try {
    const blob = await getStoryFilmPreparationSample(15)
    if (request !== sampleRequest) return
    if (sampleVideoUrl.value) URL.revokeObjectURL(sampleVideoUrl.value)
    sampleVideoUrl.value = URL.createObjectURL(blob)
  } catch {
    if (request === sampleRequest) sampleError.value = true
  } finally {
    if (request === sampleRequest) sampleLoading.value = false
  }
}

const isDetailView = computed(() => {
  if (workspaceMode.value === 'execution') return Boolean(selectedExecutionId.value)
  return Boolean(selectedPreparation.value)
})

const selectedItem = computed(() => {
  return executionItems.value.find(item => String(item.submission_id) === String(selectedExecutionId.value)) ?? null
})

let executionRequest = 0
let executionPollTimer = null
let independentRequest = 0
const executionViewMemory = new Map()
const independentViewMemory = new Map()

function isCurrentExecution(request) {
  return request === executionRequest
}

function handleDirectLookup() {
  const id = directInputId.value.trim()
  if (!id || !/^\d+$/.test(id)) return
  openExecution(id)
  directInputId.value = ''
}

function openExecution(id) {
  const targetId = String(id).trim()
  if (!/^\d+$/.test(targetId)) return
  stopExecutionPolling()
  workspaceMode.value = 'execution'
  selectedExecutionId.value = targetId
  void loadExecutionData(targetId)
}

async function loadExecutionData(id) {
  const request = ++executionRequest
  executionLoading.value = true
  executionError.value = ''
  execution.value = null
  selectedShotIndex.value = null
  clearVideo()
  clearReferenceUrls()

  try {
    const result = await getStoryFilmExecution(id)
    if (!isCurrentExecution(request)) return
    reconcileReferences(result.shots)
    execution.value = result
    mediaState.value = result.media ? 'loading' : 'not-recorded'
    const remembered = executionViewMemory.get(id)
    const shot = result.shots.find(item => item.index === remembered?.shotIndex) ?? result.shots[0]
    selectedShotIndex.value = shot?.index ?? null
    executionLoading.value = false

    await Promise.all([
      loadExecutionCurrency(result),
      loadExecutionMedia(id, result, request),
      loadSelectedReferences(id, shot),
    ])
  } catch (cause) {
    if (isCurrentExecution(request)) executionError.value = recordErrorText(cause)
  } finally {
    if (isCurrentExecution(request)) executionLoading.value = false
  }
}

async function loadExecutionMedia(id, result, request) {
  if (!result.media) return
  try {
    const blob = await getStoryFilmExecutionMedia(id)
    if (!isCurrentExecution(request)) return
    if (executionVideoUrl.value) URL.revokeObjectURL(executionVideoUrl.value)
    executionVideoUrl.value = URL.createObjectURL(blob)
  } catch (cause) {
    if (isCurrentExecution(request)) {
      mediaState.value = 'error'
      mediaError.value = cause?.message || '视频读取失败'
    }
  }
}

async function selectPreparation(item) {
  clearSample()
  selectedPreparation.value = item
  selectedPreparationDetail.value = null
  independentRecord.value = null
  independentLoading.value = true
  independentError.value = ''
  try {
    const detail = await getStoryFilmAgentPreparation(item.preparation_id)
    selectedPreparationDetail.value = detail
  } catch (cause) {
    independentError.value = cause?.message || '加载准备记录失败'
  } finally {
    independentLoading.value = false
  }
}

function loadPreparationReference(index) {
  if (!selectedPreparation.value) return Promise.reject(new Error('未选择准备记录'))
  return getStoryFilmPreparationReference(selectedPreparation.value.preparation_id, index)
}

function returnToList() {
  clearSample()
  selectedExecutionId.value = ''
  execution.value = null
  selectedPreparation.value = null
  selectedPreparationDetail.value = null
  stopExecutionPolling()
  clearVideo()
  clearReferenceUrls()
}

function statusTone(status) {
  if (status === 'failed' || status === 'rejected' || status === 'error') return 'danger'
  if (status === 'media_ready' || status === 'ok') return 'success'
  if (status === 'producing' || status === 'running') return 'info'
  return 'neutral'
}

function executionStatus(status) {
  const known = ['queued', 'producing', 'failed', 'review_ready', 'rejected', 'media_ready']
  return t(`storyFilm.executionLog.${known.includes(status) ? `status_${status}` : 'status_unknown'}`)
}

onMounted(() => {
  loadExecutions(true)
  loadPreparations(true)
  if (route.query.submission_id) openExecution(route.query.submission_id)
})

onUnmounted(() => {
  clearSample()
  disposeDetail()
  clearVideo()
})
</script>

<template>
  <div class="review-workbench vga-page" data-insp-path="apps/visual-game-admin/src/pages/StoryFilmPromptReview.vue">
    <!-- 1. 列表模式 (List View，清晰宽表管理海量任务) -->
    <template v-if="!isDetailView">
      <header class="workbench-header">
        <div class="workbench-header__left">
          <PageHeader class="workbench-header__title" :title="t('storyFilm.executionLog.title')" />
          <span class="workbench-header__sub">{{ t('storyFilm.executionLog.workspaceIntro') }}</span>
        </div>
        <div class="workbench-header__right">
          <Badge variant="info" size="sm">只读核对</Badge>
          <Button size="sm" variant="secondary" @click="showHistoryDrawer = true">
            <Clock :size="13" />
            <span>历史快照版本</span>
          </Button>
          <Button
            size="sm"
            variant="secondary"
            :disabled="executionListLoading || preparationLoading"
            @click="loadExecutions(true); loadPreparations(true)"
          >
            <RefreshCw :size="13" :class="{ 'is-spinning': executionListLoading || preparationLoading }" />
            <span>{{ t('visualGameAdmin.novelRuns.audit.refresh') }}</span>
          </Button>
        </div>
      </header>

      <!-- 任务列表组件 (已独立解耦) -->
      <StoryFilmListView
        v-model:workspace-mode="workspaceMode"
        v-model:view-mode="viewMode"
        v-model:selected-film="selectedFilm"
        v-model:selected-status-filter="selectedStatusFilter"
        v-model:search-keyword="searchKeyword"
        v-model:direct-input-id="directInputId"
        :execution-items="executionItems"
        :filtered-executions="filteredExecutions"
        :grouped-executions="groupedExecutions"
        :execution-list-loading="executionListLoading"
        :execution-list-error="executionListError"
        :preparations="preparations"
        :filtered-preparations="filteredPreparations"
        :preparation-loading="preparationLoading"
        :preparation-error="preparationError"
        :film-options="filmOptions"
        :is-filter-active="isFilterActive"
        @reset-filters="resetFilters"
        @direct-lookup="handleDirectLookup"
        @open-execution="openExecution"
        @open-preparation="selectPreparation"
        @retry-execution="loadExecutions(true)"
        @retry-preparation="loadPreparations(true)"
      />
    </template>

    <!-- 2. 详情审查工作台 (Detail View，100% 满屏无侧栏，沉浸式对照) -->
    <template v-else>
      <!-- 详情页顶栏导航与面包屑 -->
      <header class="detail-nav-header">
        <div class="nav-header-left">
          <Button size="sm" variant="secondary" @click="returnToList">
            <ArrowLeft :size="14" />
            <span>返回任务列表</span>
          </Button>
          <div class="nav-breadcrumbs">
            <template v-if="workspaceMode === 'execution'">
              <span class="nav-film-title">{{ selectedItem?.film_title || '制作执行' }}</span>
              <span class="nav-sep">/</span>
              <span>第 {{ selectedItem?.season_no ?? '—' }} 季 第 {{ selectedItem?.episode_no ?? '—' }} 集</span>
              <span class="nav-sep">/</span>
              <span class="nav-sub-id">受理 #{{ execution?.submission_id || selectedExecutionId }}</span>
              <Badge v-if="execution" :variant="statusTone(execution.status)" size="sm">
                {{ executionStatus(execution.status) }}
              </Badge>
            </template>
            <template v-else>
              <span class="nav-film-title">{{ selectedPreparationDetail?.film_title || '样片准备' }}</span>
              <span class="nav-sep">/</span>
              <span>编号 #{{ selectedPreparation?.preparation_id }}</span>
              <Badge v-if="selectedPreparation" :variant="statusTone(selectedPreparation.status)" size="sm">
                {{ selectedPreparation.status }}
              </Badge>
            </template>
          </div>
        </div>
        <div class="nav-header-right">
          <Button
            v-if="workspaceMode === 'execution' && selectedExecutionId"
            size="sm"
            variant="secondary"
            :disabled="executionLoading"
            @click="loadExecutionData(selectedExecutionId)"
          >
            <RefreshCw :size="13" :class="{ 'is-spinning': executionLoading }" />
            <span>{{ t('visualGameAdmin.novelRuns.audit.refresh') }}</span>
          </Button>
          <Button
            v-else-if="workspaceMode === 'independent' && selectedPreparation"
            size="sm"
            variant="secondary"
            :disabled="independentLoading"
            @click="selectPreparation(selectedPreparation)"
          >
            <RefreshCw :size="13" :class="{ 'is-spinning': independentLoading }" />
            <span>{{ t('visualGameAdmin.novelRuns.audit.refresh') }}</span>
          </Button>
        </div>
      </header>

      <!-- 详情事实指示条 (仅受理制作) -->
      <div v-if="workspaceMode === 'execution' && execution" class="facts-bar">
        <span class="fact-pill"><b>{{ execution.shots.length }} 镜全部排定</b></span>
        <span class="fact-pill"><b>实际切点</b> 未记录（以计划区间近似）</span>
        <span class="fact-pill"><b>剧本</b> {{ execution.screenplay ? '结构化留存' : '未留存' }}</span>
        <span class="fact-pill">提示词 <b>{{ !selectedShot?.call ? t('storyFilmReview.notSent') : (promptComparison === 'same' ? '计划 = 实发' : '实发已留存') }}</b></span>
        <span class="fact-pill">调用状态 <b>{{ execution.shots.filter(s => s.call?.status === 'ok').length }}/{{ execution.shots.length }} ok</b></span>
      </div>

      <!-- 全宽详情主体工作区 (无左栏！通栏平铺！) -->
      <div class="detail-workbench-grid" :class="{ 'is-independent': workspaceMode === 'independent' }">
        <!-- 样片准备全宽面板 (已独立解耦) -->
        <template v-if="workspaceMode === 'independent'">
          <div v-if="independentLoading" class="center-state" role="status">
            <RefreshCw class="spin-icon" :size="24" />
            <span>{{ t('storyFilmReview.loading') }}</span>
          </div>
          <div v-else-if="independentError" class="center-state" role="alert">
            <AlertTriangle :size="32" class="error-icon" />
            <p class="error-msg">{{ independentError }}</p>
            <Button size="sm" variant="secondary" @click="selectPreparation(selectedPreparation)">{{ t('visualGameAdmin.common.retry') }}</Button>
          </div>
          <template v-else-if="selectedPreparation && selectedPreparationDetail">
            <section v-if="Number(selectedPreparation.preparation_id) === 15" class="internal-sample" data-testid="preparation-internal-sample">
              <h2>{{ t('storyFilmReview.internalSampleTitle') }}</h2>
              <p>{{ t('storyFilmReview.internalSampleNotice') }}</p>
              <Button v-if="!sampleVideoUrl" :disabled="sampleLoading" @click="loadSample">{{ t(sampleLoading ? 'storyFilmReview.internalSampleLoading' : 'storyFilmReview.internalSampleLoad') }}</Button>
              <p v-if="sampleError" role="alert">{{ t('storyFilmReview.internalSampleError') }}</p>
              <Button v-if="sampleVideoUrl && !samplePlaying" @click="playSample">{{ t('storyFilmReview.internalSamplePlay') }}</Button>
              <p v-if="samplePlaying" role="status">{{ t('storyFilmReview.internalSamplePlaying') }}</p>
              <video v-if="sampleVideoUrl" ref="sampleVideo" :src="sampleVideoUrl" controls playsinline preload="metadata" :aria-label="t('storyFilmReview.internalSampleTitle')" @playing="samplePlaying = true" @pause="samplePlaying = false" @ended="samplePlaying = false" @error="sampleError = true" />
            </section>
            <PreparationOverview
              :record="selectedPreparationDetail"
              :title="selectedPreparationDetail.film_title || '制作记录'"
              :scope-label="selectedPreparationDetail.preparation_scope || '全局'"
              :status-label="selectedPreparationDetail.status"
              :load-reference="loadPreparationReference"
              @refresh="selectPreparation(selectedPreparation)"
              @open-submission="openExecution"
            />
          </template>
          <div v-else class="center-state">{{ t('storyFilmReview.choosePreparation') }}</div>
        </template>

        <!-- 受理制作双列全宽审查台 -->
        <template v-else>
          <!-- 左列：放映厅、时间轴、镜头导航 (已独立解耦) -->
          <div class="stage-column">
            <div v-if="executionLoading && !execution" class="center-state" role="status">
              <RefreshCw class="spin-icon" :size="28" />
              <span>{{ t('storyFilm.executionLog.recordLoading') }}</span>
            </div>
            <div v-else-if="executionError" class="center-state" role="alert">
              <AlertTriangle :size="32" class="error-icon" />
              <p class="error-msg">{{ executionError }}</p>
              <Button size="sm" variant="secondary" @click="loadExecutionData(selectedExecutionId)">{{ t('visualGameAdmin.common.retry') }}</Button>
            </div>
            <div v-else-if="!execution" class="center-state">
              <Film :size="48" class="empty-icon" />
              <p>{{ t('storyFilm.executionLog.chooseTask') }}</p>
            </div>
            <StoryFilmDetailStage
              v-else
              :execution="execution"
              :selected-shot-index="selectedShotIndex"
              :selected-shot="selectedShot"
              :execution-video-url="executionVideoUrl"
              :current-video-time="currentVideoTime"
              :total-shots-duration-ms="totalShotsDurationMs"
              :playhead-percent="playheadPercent"
              :media-state="mediaState"
              :cast-role-list="castRoleList"
              :prompt-comparison="promptComparison"
              @select-shot="selectShot"
              @toggle-play="togglePlay"
              @seek-planned-shot="seekPlannedShot"
              @time-update="onTimeUpdate"
              @video-metadata="onVideoMetadata"
              @video-ready="onVideoReady"
              @video-error="onVideoError"
            />
          </div>

          <!-- 右列：分镜要素审查台 -->
          <aside class="inspector-column">
            <StoryFilmShotInspector
              v-if="selectedShot"
              v-model:selected-detail-tab="selectedDetailTab"
              :shot="selectedShot"
              :execution="execution"
              :reference-urls="referenceUrls"
              :reference-states="referenceStates"
              :reference-errors="referenceErrors"
              :source-story-href="sourceStoryHref"
              :evidence-groups="evidenceGroups"
              :frozen-passages="frozenPassages"
              :selected-scene="selectedScene"
              :predicted-currency="predictedCurrency"
              @preview-image="previewImageModal = $event"
            />
          </aside>
        </template>
      </div>
    </template>

    <!-- 历史快照版本抽屉 (Drawer) -->
    <Drawer
      v-model:open="showHistoryDrawer"
      title="Prompt Pack 历史快照与版本管理"
      width="960px"
      @close="showHistoryDrawer = false"
    >
      <PromptPackHistory />
    </Drawer>

    <!-- 图片大图预览 (Modal) -->
    <Modal :open="Boolean(previewImageModal)" :title="t('storyFilmReview.previewImage')" @close="previewImageModal = null">
      <div class="lightbox-wrap">
        <img v-if="previewImageModal" :src="previewImageModal" alt="Reference full preview" class="lightbox-img" />
      </div>
    </Modal>
  </div>
</template>

<style scoped>
.internal-sample { margin-bottom: 1rem; padding: 1rem; background: var(--drama-color-surface); border: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-md); }
.internal-sample h2 { margin: 0 0 .5rem; font-size: 1rem; }
.internal-sample p { color: var(--drama-color-text-muted); }
.internal-sample video { display: block; max-width: 100%; max-height: 70vh; background: var(--drama-color-surface); }
.review-workbench {
  display: flex;
  flex-direction: column;
  gap: var(--drama-space-md);
  padding: var(--drama-space-md) var(--drama-space-lg);
  color: var(--drama-color-text);
  max-width: 1720px;
  margin: 0 auto;
  width: 100%;
}

.workbench-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--drama-space-md);
  flex-wrap: wrap;
}

.workbench-header__left {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.workbench-header__title {
  margin: 0;
  font-size: var(--drama-font-size-lg);
}

.workbench-header__sub {
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
}

.workbench-header__right {
  display: flex;
  align-items: center;
  gap: var(--drama-space-sm);
}

.detail-nav-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: var(--drama-space-md);
  padding: 10px 14px;
  background: var(--drama-color-surface);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-md);
  box-shadow: var(--drama-shadow-xs);
}

.nav-header-left {
  display: flex;
  align-items: center;
  gap: var(--drama-space-md);
  flex-wrap: wrap;
}

.nav-breadcrumbs {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: var(--drama-font-size-sm);
  color: var(--drama-color-text-muted);
}

.nav-film-title {
  font-weight: 700;
  font-size: var(--drama-font-size-base, 15px);
  color: var(--drama-color-text);
}

.nav-sub-id {
  font-family: monospace;
  font-weight: 600;
  color: var(--drama-color-text);
}

.nav-sep {
  color: var(--drama-color-border);
  user-select: none;
}

.nav-header-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.facts-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 6px 12px;
  background: var(--drama-color-surface-alt);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-sm);
}

.fact-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: var(--drama-color-surface);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-sm, 6px);
  padding: 4px 10px;
  font-size: var(--drama-font-size-xs);
  color: var(--drama-color-text-muted);
}

.fact-pill b {
  color: var(--drama-color-text);
}

.detail-workbench-grid {
  display: grid;
  grid-template-columns: minmax(460px, 1.15fr) 480px;
  gap: 16px;
  align-items: start;
  width: 100%;
}

.detail-workbench-grid.is-independent {
  display: flex;
  flex-direction: column;
  width: 100%;
}

@media (max-width: 1180px) {
  .detail-workbench-grid {
    grid-template-columns: 1fr;
  }
}

.stage-column {
  display: flex;
  flex-direction: column;
  gap: var(--drama-space-md);
  min-width: 0;
}

.inspector-column {
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 16px;
}

.lightbox-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  max-height: 80vh;
}

.lightbox-img {
  max-width: 100%;
  max-height: 75vh;
  object-fit: contain;
  border-radius: var(--drama-radius-sm);
}

.center-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--drama-space-md);
  min-height: 380px;
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-sm);
}

.spin-icon {
  animation: spin 1s linear infinite;
  color: var(--drama-color-primary);
}

.is-spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.error-icon {
  color: var(--drama-color-danger);
}

.error-msg {
  color: var(--drama-color-danger);
  margin: 0;
}

.empty-icon {
  opacity: 0.25;
}
</style>
